import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import IconArrow from "../../assets/images/icon-arrow-3.svg";

dayjs.extend(buddhistEra);

const THAI_MONTHS = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน",
    "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
    "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const THAI_WEEKDAYS = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];

const Container = styled.div`
    width: 100%;

    .label {
        color: #2A2A2A;
        font-size: 15px;
        font-weight: 500;
        line-height: 22.43px;
        text-align: left;
        margin-bottom: 8px;
    }
`;

const Field = styled.div`
    box-sizing: border-box;
    width: 100%;
    min-height: 40px;
    position: relative;
    background-color: #F5F5F5;
    border-radius: 30px;
    padding: 0px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s ease;

    &.disabled {
        cursor: default;
        background-color: #B3B3B333;
    }

    .placeholder {
        color: #838BB4;
        font-size: 15px;
        font-weight: 400;
    }

    .value {
        color: #25368D;
        font-size: 15px;
        font-weight: 500;
    }

    .clear-btn {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background-color: #E5E5E5;
        color: #505050;
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 8px;

        &:hover {
            background-color: #FBE250;
            color: #25368D;
        }
    }

    .arrow {
        width: 16px;
        height: 16px;
        margin-left: auto;
    }
`;

const Popover = styled.div`
    width: 300px;
    position: absolute;
    top: 48px;
    left: 0px;
    z-index: 99;
    background-color: #FFFFFF;
    border: 1px solid #B5B5B533;
    box-shadow: 0px 4px 12px 0px #60606020;
    border-radius: 12px;
    padding: 16px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .title {
            color: #25368D;
            font-size: 15px;
            font-weight: 600;
        }

        .nav {
            display: flex;
            gap: 4px;

            button {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                border: none;
                background-color: #F5F5F5;
                cursor: pointer;
                font-size: 14px;
                color: #25368D;

                &:hover { background-color: #FBE25099; }
            }
        }
    }

    .weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        margin-bottom: 4px;

        div {
            text-align: center;
            font-size: 12px;
            color: #838BB4;
            font-weight: 500;
            padding: 4px 0px;
        }
    }

    .days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;

        .day {
            height: 32px;
            border-radius: 6px;
            font-size: 13px;
            color: #2A2A2A;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.1s ease;

            &.other-month { color: #CCCCCC; }
            &:hover:not(.disabled) { background-color: #FBE25099; }

            &.in-range {
                background-color: #F2F5FE;
                border-radius: 0px;
                color: #25368D;
            }

            &.start, &.end {
                background-color: #25368D;
                color: #FFFFFF;
                font-weight: 600;
            }

            &.start { border-radius: 16px 0px 0px 16px; }
            &.end { border-radius: 0px 16px 16px 0px; }
            &.start.end { border-radius: 16px; }
        }
    }

    .footer {
        margin-top: 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .hint {
            font-size: 12px;
            color: #838BB4;
        }

        .actions {
            display: flex;
            gap: 8px;

            button {
                height: 30px;
                padding: 0px 14px;
                border-radius: 16px;
                border: 1px solid #25368D;
                font-size: 13px;
                cursor: pointer;
                background-color: #FFFFFF;
                color: #25368D;

                &.primary {
                    background-color: #25368D;
                    color: #FFFFFF;
                }

                &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            }
        }
    }
`;

function formatDisplay(date) {
    const d = dayjs(date);
    return d.locale("th").format("DD/MM/BBBB");
}

function buildGrid(viewMonth) {
    const firstOfMonth = viewMonth.startOf("month");
    const startOfGrid = firstOfMonth.subtract(firstOfMonth.day(), "day");
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const d = startOfGrid.add(i, "day");
        cells.push({
            date: d,
            inMonth: d.month() === viewMonth.month(),
        });
    }
    return cells;
}

function DateRangePickerComponent({
    label,
    value,
    placeholder,
    disable,
    clearable = true,
    onChange,
}) {
    const start = value?.start ? dayjs(value.start) : null;
    const end = value?.end ? dayjs(value.end) : null;
    const [open, setOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(dayjs());
    const [pendingStart, setPendingStart] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);
    const popoverRef = useRef(null);
    const fieldRef = useRef(null);

    useEffect(() => {
        function onOutside(e) {
            if (popoverRef.current
                && fieldRef.current
                && !popoverRef.current.contains(e.target)
                && !fieldRef.current.contains(e.target)) {
                setOpen(false);
                setPendingStart(null);
            }
        }
        window.addEventListener("click", onOutside);
        return () => window.removeEventListener("click", onOutside);
    }, []);

    useEffect(() => {
        if (open) {
            setViewMonth(start || dayjs());
            setPendingStart(null);
            setHoverDate(null);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const cells = useMemo(() => buildGrid(viewMonth), [viewMonth]);

    function isSameDay(a, b) {
        return a && b && a.isSame(b, "day");
    }

    // Effective range for rendering:
    //   when awaiting second click: [min(pendingStart, hover), max(pendingStart, hover)]
    //   otherwise: [start, end]
    let rangeStart = start;
    let rangeEnd = end;
    if (pendingStart) {
        if (hoverDate) {
            rangeStart = hoverDate.isBefore(pendingStart, "day") ? hoverDate : pendingStart;
            rangeEnd = hoverDate.isBefore(pendingStart, "day") ? pendingStart : hoverDate;
        } else {
            rangeStart = pendingStart;
            rangeEnd = null;
        }
    }

    function isInRange(d) {
        if (!rangeStart || !rangeEnd) return false;
        return d.isAfter(rangeStart, "day") && d.isBefore(rangeEnd, "day");
    }

    function handleClickDay(d) {
        if (disable) return;
        if (!pendingStart) {
            setPendingStart(d);
            onChange && onChange({ start: d.toDate(), end: null });
        } else {
            const [from, to] = d.isBefore(pendingStart, "day")
                ? [d, pendingStart]
                : [pendingStart, d];
            onChange && onChange({ start: from.toDate(), end: to.toDate() });
            setPendingStart(null);
            setOpen(false);
        }
    }

    function handleClear(e) {
        e.stopPropagation();
        setPendingStart(null);
        onChange && onChange({ start: null, end: null });
    }

    function displayText() {
        if (start && end) return `${formatDisplay(start)} - ${formatDisplay(end)}`;
        if (start) return `${formatDisplay(start)} - ...`;
        return "";
    }

    const hint = pendingStart
        ? "เลือกวันสิ้นสุด"
        : (start && end ? "" : "เลือกวันเริ่มต้น");

    return (
        <Container>
            {label && <div className="label">{label}</div>}
            <div style={{ position: "relative" }}>
                <Field
                    ref={fieldRef}
                    className={disable ? "disabled" : ""}
                    onClick={() => !disable && setOpen(!open)}
                >
                    {start ?
                        <div className="value">{displayText()}</div>
                        :
                        <div className="placeholder">{placeholder || "เลือกช่วงวันที่"}</div>
                    }
                    {start && clearable ?
                        <div className="clear-btn" onClick={handleClear} title="ล้าง">×</div>
                        :
                        <img className="arrow" alt="arrow" src={IconArrow} />
                    }
                </Field>
                {open && (
                    <Popover ref={popoverRef}>
                        <div className="header">
                            <div className="nav">
                                <button onClick={() => setViewMonth(viewMonth.subtract(1, "year"))}>«</button>
                                <button onClick={() => setViewMonth(viewMonth.subtract(1, "month"))}>‹</button>
                            </div>
                            <div className="title">
                                {THAI_MONTHS[viewMonth.month()]} {viewMonth.locale("th").format("BBBB")}
                            </div>
                            <div className="nav">
                                <button onClick={() => setViewMonth(viewMonth.add(1, "month"))}>›</button>
                                <button onClick={() => setViewMonth(viewMonth.add(1, "year"))}>»</button>
                            </div>
                        </div>
                        <div className="weekdays">
                            {THAI_WEEKDAYS.map(w => <div key={w}>{w}</div>)}
                        </div>
                        <div className="days" onMouseLeave={() => setHoverDate(null)}>
                            {cells.map((cell, i) => {
                                const isStart = isSameDay(cell.date, rangeStart);
                                const isEnd = isSameDay(cell.date, rangeEnd);
                                const inRange = isInRange(cell.date);
                                const cls = [
                                    "day",
                                    cell.inMonth ? "" : "other-month",
                                    isStart ? "start" : "",
                                    isEnd ? "end" : "",
                                    inRange ? "in-range" : "",
                                ].filter(Boolean).join(" ");
                                return (
                                    <div
                                        key={i}
                                        className={cls}
                                        onClick={() => handleClickDay(cell.date)}
                                        onMouseEnter={() => setHoverDate(cell.date)}
                                    >
                                        {cell.date.date()}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="footer">
                            <div className="hint">{hint}</div>
                            <div className="actions">
                                <button onClick={handleClear}>ล้าง</button>
                                <button className="primary" onClick={() => setOpen(false)}>ปิด</button>
                            </div>
                        </div>
                    </Popover>
                )}
            </div>
        </Container>
    );
}

export default DateRangePickerComponent;
